/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { EditOutlined, InboxOutlined, MoreOutlined } from '@ant-design/icons';

import { Subtenant } from '@/client';
import { useAuthorization } from '@/hooks/useAuthorization';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Button } from '@/primitives/Button';
import { Dropdown, MenuProps } from '@/primitives/Dropdown';
import { Space } from '@/primitives/Space';

enum ActionType {
  DELETE = 'delete',
  EDIT = 'edit',
}

export type SubtenantActionDropdownParams = {
  subtenant: Subtenant;
  onDeleteSubtenant: (subtenant: Subtenant) => void;
  editSubtenant: (subtenant: Subtenant) => void;
};

export function SubtenantActionDropdown({
  subtenant,
  onDeleteSubtenant,
  editSubtenant,
}: SubtenantActionDropdownParams) {
  const { t } = useMimicTranslation('subtenants');
  const handleActionClick: MenuProps['onClick'] = (e) => {
    switch (e.key) {
      case ActionType.EDIT:
        editSubtenant(subtenant);
        break;
      case ActionType.DELETE:
        onDeleteSubtenant(subtenant);
        break;
      default:
        break;
    }
  };

  const canManageSubtenants = useAuthorization('editor');
  if (!canManageSubtenants) return null;

  const items: MenuProps['items'] = [
    {
      label: (
        <Space dataTestId="edit-subtenant">
          <EditOutlined />
          {t('actions.editSubtenantButton')}
        </Space>
      ),
      key: ActionType.EDIT,
    },
    {
      label: (
        <Space dataTestId="delete-subtenant">
          <InboxOutlined />
          {t('actions.deleteSubtenant')}
        </Space>
      ),
      danger: true,
      key: ActionType.DELETE,
    },
  ];

  const menu = {
    items,
    onClick: handleActionClick,
  };

  return (
    <Dropdown menu={menu}>
      <Button type="text" dataTestId="subtenant-actions">
        <MoreOutlined />
      </Button>
    </Dropdown>
  );
}
